/** Interactive grid list with keyboard navigation and optional selection. Wraps React Aria GridList. */
import {
  GridList as AriaGridList,
  GridListItem as AriaGridListItem,
  type GridListProps as AriaGridListProps,
  type GridListItemProps as AriaGridListItemProps,
} from 'react-aria-components';
import { cn } from '@/foundation/utils/cn';

interface GridListProps<T extends object> extends AriaGridListProps<T> {
  className?: string;
}

export function GridList<T extends object>({ className, ...props }: GridListProps<T>) {
  return <AriaGridList className={cn('outline-none flex flex-col gap-2', className)} {...props} />;
}

interface GridListItemProps extends AriaGridListItemProps {
  className?: string;
  /** Skip default card styling — the consumer controls all visual classes. */
  unstyled?: boolean;
}

export function GridListItem({ className, unstyled, ...props }: GridListItemProps) {
  return (
    <AriaGridListItem
      className={cn(
        unstyled
          ? 'outline-none focus-visible:ring-2 focus-visible:ring-[var(--f-accent)] focus-visible:ring-offset-1'
          : [
              'outline-none rounded-2xl border border-[var(--f-border)] bg-[var(--f-bg-raised)]',
              'transition-all duration-150 cursor-pointer',
              'hover:shadow-[var(--f-shadow-sm)] hover:border-[var(--f-border-strong)]',
              'focus-visible:ring-2 focus-visible:ring-[var(--f-accent)] focus-visible:ring-offset-1',
              'data-[selected]:border-[var(--f-accent)] data-[selected]:bg-[var(--f-accent-subtle)]',
            ],
        className
      )}
      {...props}
    />
  );
}
